import BookmarkButton from '@/components/BookmarkButton'
import Button from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import Text from '@/components/Text'
import Avatar from '@/components/V2/Avatar'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import { getConfig } from '@/lib/config'
import { formatDuration } from '@/lib/datetime'
import { AIElementNames, eventsTracker } from '@/lib/eventsTracker'
import { ItemType, VodClassCard } from '@/shared/api-generated-types'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as style from './style'

export interface Props extends Omit<VodClassCard, '__typename'> {
  redirectToLobby?: boolean
}

const VODClassCard = (props: Props): JSX.Element => {
  const config = getConfig()
  const { t } = useTranslation('classes')
  const { state: bookmarkState } = useBookmarkItems()
  const [bookmarked, setBookmarked] = useState<boolean | undefined>(props.bookmarked)
  const router = useRouter()

  useEffect(() => {
    const itemId = props.id
    if (itemId && bookmarkState.classes?.[itemId] === true) {
      setBookmarked(true)
    } else if (itemId && bookmarkState.classes?.[itemId] === false) {
      setBookmarked(false)
    }
  }, [bookmarkState, props.id])

  const detailsPageLink = props.redirectToLobby
    ? `${config.publicRuntimeConfig.lobby.url}/class/${props.slug}/${props.id}/vod`
    : `${config.publicRuntimeConfig.website.url}/helen-recommendations/watch/${props.id}`

  const handleClick = (event: React.MouseEvent): void => {
    event.preventDefault()
    eventsTracker.handleOnClickAndTrackEvent(AIElementNames.BUTTON_CLASS_CARD)
    router.push(detailsPageLink)
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg relative flex flex-col">
      <a href={detailsPageLink} className="flex flex-col h-full">
        <div className={`${style.imageContainer}`}>
          <img
            alt={'thumbnail'}
            src={props.imageUrl ?? '/images/image-placeholder-large.png'}
            className={style.image}
            loading="lazy"
            decoding="async"
          />
        </div>

        {props.guideAvatarUrl && (
          <Avatar className="ml-3 mt-[-48px] border-white z-2" size="xsmd" src={props.guideAvatarUrl} outlined />
        )}

        <div className="relative">
          <BookmarkButton
            bookmarked={bookmarked}
            size="md"
            itemId={props.id}
            itemType={ItemType.Class}
            className="absolute top-[-2.5em] right-4 rounded"
          />
        </div>

        <div className="flex flex-col flex-1 justify-between h-full">
          <div className="p-4">
            <Text tag="h2" title={props.title} size="sm" weight="lg" className={`text-[#2d2d32] ellipsis-2L`}>
              {props.title}
            </Text>
            <p className="text-sm text-[#757582] mb-4">{props.guideName}</p>
          </div>

          <div className="flex items-center justify-between px-4 pb-4 mt-auto">
            {props.durationSeconds && (
              <div className="flex items-center text-[#2D2D32]">
                <IcoMoon icon="youtube" size={16} className="mr-xxxs" />
                <span className="text-sm">{formatDuration(props.durationSeconds)}</span>
              </div>
            )}
            <Button onClick={handleClick} size="sm" theme="primaryOutlineHover" label={t('slotCard.watchNow')} />
          </div>
        </div>
      </a>
    </div>
  )
}

export default VODClassCard
